// AppointmentsMobile.jsx — onglet Rendez-vous (mobile) en LECTURE (agenda).
// Les RDV gèrent des statuts + conversion en vente/paiement
// (cf. GestionRendezVous.jsx) : la création/édition reste sur desktop.
// Ici : agenda groupé par jour, filtres de statut, fiche détail.
import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Sheet,
  DetailRow, Note, Mono, Pill, Ico,
  useResourceList, useMobileMenu, fmtMoney,
} from "./productMobileKit";

const toCompanyId = (c) => c?.id ?? (c ? Number(c) : null);
const STATUS = {
  scheduled: { tone: "info", label: "Programmé" },
  confirmed: { tone: "cyan", label: "Confirmé" },
  in_progress: { tone: "warn", label: "En cours" },
  completed: { tone: "ok", label: "Terminé" },
  cancelled: { tone: "danger", label: "Annulé" },
  no_show: { tone: "off", label: "Absent" },
};
const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const dayLabel = (d) => {
  const t = dayjs();
  if (d.isSame(t, "day")) return "Aujourd’hui";
  if (d.isSame(t.add(1, "day"), "day")) return "Demain";
  return `${d.date()} ${MONTHS[d.month()]}`;
};
const svcName = (a) => a.service_name || a.service?.name || "Service";

export default function AppointmentsMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const { hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const canView = hasPermission("Gestion Commerciale.RendezVous.view");
  const companyId = toCompanyId(selectedCompany);

  const { rows: appts, loading } = useResourceList(
    async () => {
      if (!companyId || !canView) return [];
      const res = await apiClient.get("/api/service-appointments", {
        params: {
          company_id: companyId, page: 1, limit: 200,
          date_from: dayjs().startOf("day").toISOString(),
          date_to: dayjs().add(30, "day").endOf("day").toISOString(),
        },
      });
      return res.data?.appointments || [];
    },
    [companyId, canView]
  );

  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const upcoming = useMemo(() => appts.filter((a) => ["scheduled", "confirmed"].includes(a.status)).length, [appts]);
  const done = useMemo(() => appts.filter((a) => a.status === "completed").length, [appts]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return appts
      .filter((a) => {
        if (filter !== "all" && a.status !== filter) return false;
        if (q && ![a.customer_name, a.customer_phone, svcName(a)].some((v) => String(v || "").toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((x, y) => new Date(x.starts_at) - new Date(y.starts_at));
  }, [appts, query, filter]);

  const groups = useMemo(() => {
    const map = new Map();
    visible.forEach((a) => {
      const d = dayjs(a.starts_at);
      const key = d.format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, { label: dayLabel(d), items: [] });
      map.get(key).items.push(a);
    });
    return Array.from(map.values());
  }, [visible]);

  return (
    <ProductScreen>
      <Appbar title="Rendez-vous" onMenu={onMenu}
        subtitle={`${appts.length} sur 30 jours`}
        action={<button className="iconbtn"><Ico name="clock" /></button>} />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Summary items={[
          { label: "Total", value: appts.length },
          { label: "À venir", value: upcoming },
          { label: "Terminés", value: done },
        ]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Client, téléphone, service…" />
        <Chips value={filter} onChange={setFilter} options={[
          { key: "all", label: "Tous" }, { key: "scheduled", label: "Programmés" },
          { key: "confirmed", label: "Confirmés" }, { key: "completed", label: "Terminés" },
        ]} />
      </div>

      <div className="body">
        {loading || !visible.length ? (
          <EmptyState icon="clock" loading={loading}
            title={loading ? "Chargement des rendez-vous" : (query || filter !== "all" ? "Aucun résultat" : "Aucun rendez-vous")}
            text={loading ? "Synchronisation en cours." : "Aucun rendez-vous sur les 30 prochains jours."} />
        ) : (
          <div className="list">
            {groups.map((g) => (
              <React.Fragment key={g.label + g.items[0].id}>
                <div className="group">{g.label}<span className="group__n">· {g.items.length}</span></div>
                {g.items.map((a) => {
                  const st = STATUS[a.status] || { tone: "off", label: a.status };
                  return (
                    <button key={a.id} className="rcard" onClick={() => setDetail(a)}>
                      <Mono name={a.customer_name || "?"} />
                      <div className="rcard__main">
                        <div className="rcard__name">{a.customer_name || "Client"}</div>
                        <div className="rcard__meta">
                          <span className="rcard__sub">{dayjs(a.starts_at).format("HH:mm")} · {svcName(a)}</span>
                          <Pill tone={st.tone} label={st.label} />
                        </div>
                      </div>
                      <span className="rcard__price">{fmtMoney(a.total_price)}</span>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
            <div style={{ height: 24 }} />
          </div>
        )}
      </div>

      <Sheet open={!!detail} onClose={() => setDetail(null)} title={detail?.customer_name || "Rendez-vous"} subtitle={detail ? svcName(detail) : ""}>
        {detail ? (
          <>
            <DetailRow label="Client" value={detail.customer_name || "—"} />
            {detail.customer_phone ? <DetailRow label="Téléphone" value={detail.customer_phone} /> : null}
            <DetailRow label="Service" value={svcName(detail)} />
            <DetailRow label="Début" value={dayjs(detail.starts_at).format("DD/MM/YYYY HH:mm")} />
            {detail.duration_minutes ? <DetailRow label="Durée" value={`${detail.duration_minutes} min`} /> : null}
            <DetailRow label="Montant" value={fmtMoney(detail.total_price)} />
            <DetailRow label="Statut" value={<Pill {...(STATUS[detail.status] || { tone: "off", label: detail.status })} />} />
            {detail.notes ? <DetailRow label="Notes" value={detail.notes} /> : null}
            <Note>La prise de rendez-vous, le changement de statut et l’encaissement se font sur la version ordinateur.</Note>
          </>
        ) : null}
      </Sheet>
    </ProductScreen>
  );
}
